import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TooltipDisplayService } from '../../../services/tooltip-display.service';

let toggletipCounter = 0;
const TOOLTIP_DELAY = 400;

@Component({
  selector: 'tooltip',
  template: `
    <span
      #trigger
      class="nsw-tooltip js-tooltip"
      tabindex="0"
      [attr.aria-label]="ariaLabel"
      [attr.aria-describedby]="isOpen ? contentId : null"
      [attr.data-title]="title || null"
      (mouseenter)="onMouseEnter()"
      (mouseleave)="onMouseLeave()"
      (focus)="onFocus()"
      (blur)="onBlur()"
    >
      <ng-content></ng-content>
      <ng-container *ngIf="showIcon">
        <i *ngIf="iconClass; else defaultIcon" [class]="iconClass" aria-hidden="true"></i>
        <ng-template #defaultIcon>
          <svg class="nsw-tooltip__icon" viewBox="0 0 24 24" focusable="false" aria-hidden="true">
            <path
              fill="currentColor"
              d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm1 15h-2v-6h2v6Zm0-8h-2V7h2v2Z"
            />
          </svg>
        </ng-template>
      </ng-container>
    </span>

    <div
      #element
      class="nsw-tooltip__element"
      [id]="toggletipId"
      [class.nsw-tooltip__element--dark]="theme === 'dark'"
      [class.nsw-tooltip__element--light]="theme === 'light'"
      [class.active]="isOpen"
      role="tooltip"
    >
      <div [id]="contentId" class="nsw-tooltip__content" [innerHTML]="tooltipText"></div>
      <div #arrow class="nsw-tooltip__arrow"></div>
    </div>
  `,
  styles: [`
    :host { display: inline; font-family: inherit; }
    .nsw-tooltip {
      display: inline-flex;
      align-items: center;
      white-space: nowrap;
      cursor: help;
      margin-left: 0.5rem;
      vertical-align: middle;
    }
    .nsw-tooltip:focus { outline: 3px solid var(--nsw-focus, #0085b3); outline-offset: 2px; }
    .nsw-tooltip__icon { width: 1.25em; height: 1.25em; vertical-align: text-bottom; color: var(--nsw-brand-dark, #002664); }
    .nsw-tooltip__element {
      display: none;
      width: max-content;
      position: fixed;
      top: 0;
      left: 0;
      max-width: 260px;
      padding: 12px 16px;
      border-radius: var(--nsw-border-radius, 4px);
      box-shadow: var(--nsw-box-shadow, 0 4px 12px 0 rgba(0, 0, 0, 0.15));
      line-height: 1.5;
      font-size: 14px;
      text-align: left;
      white-space: normal;
      z-index: 2000;
      pointer-events: none;
    }
    .nsw-tooltip__element.active { display: inline-block; }
    .nsw-tooltip__element--dark { background-color: var(--nsw-grey-02, #495054); color: var(--nsw-text-light, #ffffff); }
    .nsw-tooltip__element--dark .nsw-tooltip__arrow { background-color: var(--nsw-grey-02, #495054); }
    .nsw-tooltip__element--light { background-color: var(--nsw-white, #ffffff); color: var(--nsw-text-dark, #22272b); }
    .nsw-tooltip__element--light .nsw-tooltip__arrow { background-color: var(--nsw-white, #ffffff); }
    .nsw-tooltip__arrow { position: absolute; width: 12px; height: 12px; transform: rotate(45deg); }
    .nsw-tooltip__content > :first-child { margin-top: 0; }
  `],
})
export class TooltipDisplayComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() register = '';
  @Input() tab?: string;
  @Input() section?: string;
  @Input() field = '';
  @Input() title?: string;
  @Input() theme: 'dark' | 'light' = 'dark';
  @Input() iconClass = '';
  @Input() appendToBody = false;

  @ViewChild('trigger', { static: true }) private triggerRef!: ElementRef<HTMLElement>;
  @ViewChild('element', { static: true }) private elementRef!: ElementRef<HTMLElement>;
  @ViewChild('arrow', { static: true }) private arrowRef!: ElementRef<HTMLElement>;

  readonly toggletipId = `toggletip-${++toggletipCounter}`;
  readonly contentId = `toggletip-${toggletipCounter}-content`;
  tooltipText?: string;
  isOpen = false;
  showIcon = false;

  private registerSubscription?: Subscription;
  private currentRegisterKey = '';
  private visibilityTimer?: ReturnType<typeof setTimeout>;
  private originalParent?: Node;
  private originalNextSibling?: Node | null;
  private movedToBody = false;

  constructor(
    private tooltipService: TooltipDisplayService,
    private cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  get ariaLabel(): string {
    const label = (this.title || this.triggerRef?.nativeElement.textContent || this.field || '').trim();
    return label ? `${label} tooltip` : 'tooltip';
  }

  ngOnChanges(_: SimpleChanges): void {
    if (!this.register) {
      this.tooltipText = undefined;
      return;
    }

    const context = { register: this.register, tab: this.tab, section: this.section };
    const registerKey = this.tooltipService.getRegisterKey(context);
    if (registerKey !== this.currentRegisterKey) {
      this.currentRegisterKey = registerKey;
      this.registerSubscription?.unsubscribe();
      this.registerSubscription = this.tooltipService.loadTooltip(context)
        .pipe(filter((value) => !!value))
        .subscribe(() => this.setToolTip());
    }
    this.setToolTip();
  }

  ngAfterViewInit(): void {
    this.showIcon = !this.triggerRef.nativeElement.textContent?.trim();
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.clearVisibilityTimer();
    this.registerSubscription?.unsubscribe();
  }

  onMouseEnter(): void { this.scheduleVisibility(true); }

  onMouseLeave(): void { this.scheduleVisibility(false); }

  onFocus(): void { this.scheduleVisibility(true); }

  onBlur(): void { this.scheduleVisibility(false); }

  @HostListener('document:keyup', ['$event'])
  onDocumentKeyup(event: KeyboardEvent): void {
    if (event.key === 'Escape') this.hideTooltip();
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onViewportChange(): void { this.hideTooltip(); }

  private scheduleVisibility(visible: boolean): void {
    this.clearVisibilityTimer();
    if (visible && !this.tooltipText) return;

    this.visibilityTimer = setTimeout(() => {
      if (visible) {
        this.showTooltip();
      } else {
        this.hideTooltip();
      }
    }, TOOLTIP_DELAY);
  }

  private showTooltip(): void {
    if (!this.tooltipText) return;
    this.isOpen = true;
    this.cdr.detectChanges();
    this.moveElementToBody();
    this.position();
  }

  private hideTooltip(): void {
    this.clearVisibilityTimer();
    this.isOpen = false;
    this.restoreElement();
  }

  private moveElementToBody(): void {
    const element = this.elementRef.nativeElement;
    if (!this.appendToBody || this.movedToBody || element.parentElement === this.document.body) return;

    this.originalParent = element.parentNode || undefined;
    this.originalNextSibling = element.nextSibling;
    this.document.body.appendChild(element);
    this.movedToBody = true;
  }

  private restoreElement(): void {
    if (!this.movedToBody || !this.originalParent) return;

    if (this.originalNextSibling && this.originalNextSibling.parentNode === this.originalParent) {
      this.originalParent.insertBefore(this.elementRef.nativeElement, this.originalNextSibling);
    } else {
      this.originalParent.appendChild(this.elementRef.nativeElement);
    }

    this.originalParent = undefined;
    this.originalNextSibling = undefined;
    this.movedToBody = false;
  }

  private clearVisibilityTimer(): void {
    if (this.visibilityTimer) {
      clearTimeout(this.visibilityTimer);
      this.visibilityTimer = undefined;
    }
  }

  private setToolTip(): void {
    if (!this.register || !this.field) {
      this.tooltipText = undefined;
      return;
    }
    this.tooltipText = this.tooltipService.getTooltip({
      register: this.register,
      tab: this.tab,
      section: this.section,
      field: this.field,
    });
  }

  private position(): void {
    const element = this.elementRef.nativeElement;
    const arrow = this.arrowRef.nativeElement;
    const triggerRect = this.triggerRef.nativeElement.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const placeAbove = triggerRect.top - elementRect.height - 8 >= 0;
    const top = placeAbove ? triggerRect.top - elementRect.height - 8 : triggerRect.bottom + 8;
    const maxLeft = Math.max(5, window.innerWidth - elementRect.width - 5);
    const left = Math.min(Math.max(5, triggerRect.left + (triggerRect.width - elementRect.width) / 2), maxLeft);

    element.style.top = `${top}px`;
    element.style.left = `${left}px`;

    const arrowLeft = Math.min(
      Math.max(8, triggerRect.left + triggerRect.width / 2 - left),
      Math.max(8, elementRect.width - 14)
    );

    arrow.style.left = `${arrowLeft}px`;
    arrow.style.top = placeAbove ? `${elementRect.height - 6}px` : '-6px';
  }
}